import { defineComponent, inferProps, type ReactiveComponent } from "vue-utils"
import { css } from "vite-css-in-js"
import type { id, LessonPage } from "../../models"
import BootstrapButton from "../../components/BootstrapButton"
import { reactive, ref, watch } from "vue"
import { FlashCards } from "vue3-flashcards"

interface FlashCardItemWrapper {
   activeItemKey: id
   item: FlashCardItem
}

type FlashCardItem = {
   id: id
   lesson: LessonPage
}

interface Props {
   lessons: LessonPage[]
   backToHome(): void
}

const pageStyles = css`
   background-color: #D8EFEE;
   display: flex;
   flex-direction: column;
   height: 100%;
`

const buttonStyles = css`
   display: flex;
   justify-content: flex-start;

   button {
      background-color: transparent;
      border: none;
      color: #00A89E;
      cursor: pointer;
      aspect-ratio: 1 / 1;
      padding: 0.75rem;
      border-radius:100%;
      transition: 0.2s;
      font-size: 20px;
      margin: 0 0.5rem;
      margin-top: 0.5rem;
   }

   button:hover {
      background-color: #dddddd;
      transition: 0.2s;
   }
`

const flashCardList = css`
   flex-grow: 1;
   display: flex;
   flex-direction: column;
   justify-content: center;
   align-items: center;
   padding: 0 2rem;
   gap: 2rem;
   text-align: center;

   div.flashCardWrapper {
      width: 100%;
      justify-content: center;
      align-items: center;
   }

   div.flashcards {
      height: 100% !important;
      width: 100%;
      justify-content: center;
      align-items: center;
      grid-template-columns: 1fr;
   }

   div.flashcards__card-wrapper {
      display:flex;
      flex-grow: 1;
      width: 100%;
      justify-content: center;
      align-items: center;
   }

   div.flash-card {
      width: auto;
      max-width: 100%;
      max-height: 600px;
      aspect-ratio: 3 / 4;
      box-sizing: border-box;

      div.flash-card__animation-wrapper {
         height: 100%;
         width: auto;
      }

      div.flash-card__transform {
         height: 100%;
         width: auto;
         background-color: #F1EBF5;
         box-shadow: 1px 2px 3px #a1a1a1;
         border-radius: 30px;
      }
   }
`

const flashCard = css`
   height: 100%;
   display: flex;
   flex-direction: column;
   justify-content: center;
   align-items: center;
   padding: 2rem;
   text-align: center;

   img {
      max-height: 75px;
      max-width: 75px;
      height: auto;
      width: auto;
      pointer-events: none;
   }

   p {
      margin-top: 0;
   }
`

const FlashCardsPage: ReactiveComponent<Props> = (props) => {
   const items = reactive<FlashCardItem[]>([])
   const flashcardsRef = ref()

   function restartGame() {
      Object.assign(items, props.lessons.map(lesson => ({
         id: lesson.id,
         lesson,
      })))
      flashcardsRef.value?.reset()
   }

   function handleSwipeLeft(lesson: FlashCardItem) {
      const retryIndex = items.findIndex((l) => l.id === lesson.id)
      items.splice(0, retryIndex + 1)
      items.push(lesson)
      flashcardsRef.value?.reset()
   }

   watch(() => props.lessons, newLessons => {
         Object.assign(items, newLessons.map(lesson => ({
            id: lesson.id,
            lesson,
         })))
      },{ immediate: true }
   )

	return () => (
      <div class={pageStyles}>
         <div class={buttonStyles}>
            <BootstrapButton color="decider" title="Back to Home" onClick={props.backToHome}><i class="fas fa-arrow-left" /></BootstrapButton>
         </div>
         <div class={flashCardList}>
            <div class={buttonStyles}>
            <BootstrapButton color="decider" title="Restart Game" onClick={restartGame}><i class="fas fa-redo" /></BootstrapButton>
            </div>
            <FlashCards ref={flashcardsRef} items={items} stack={2} class="flashCardWrapper" onSwipeLeft={handleSwipeLeft}>
               {{
                  default: (item: FlashCardItemWrapper) => (
                     <div class={`${flashCard} card`}>
                        {item.item.lesson.properties.image && (
                           <img src={`https://pwa-admin.thedecider.org.uk${item.item.lesson.properties.image[0]?.url}`} alt={item.item.lesson.properties.image[0]?.name}/>
                        )}
                        <h3>{item.item.lesson.name}</h3>
                        <p v-html={item.item.lesson.properties.introduction?.markup}></p>
                     </div>
                  )
               }}
            </FlashCards>
            <p>Study the cards. Swipe left to put the card back in the deck. Swipe right to remove the card from the deck.</p>
         </div>
      </div>
	)
}
export default defineComponent(FlashCardsPage, inferProps<Props>())